import type { CatalogDocumentDTO, ReferenceType } from '@/shared/api'
import {
  ACCEPTED_FILE_EXTENSIONS,
  ACCEPTED_FILE_LABEL,
  MAX_DOCUMENT_ID_LENGTH,
  deriveDocumentIdFromFilename,
  formatBytes,
  formatDate,
  statusLabel,
  statusTone,
} from '@/features/documents/documentUtils'
import type { DocumentStatusTone } from '@/features/documents/documentUtils'
import { REFERENCE_TYPE_OPTIONS } from './types'

/**
 * Reference Library utilities. Reuses date/byte/status helpers from the
 * Documents Library (DRY) — we only add reference-specific helpers here.
 */

export {
  ACCEPTED_FILE_EXTENSIONS,
  ACCEPTED_FILE_LABEL,
  MAX_DOCUMENT_ID_LENGTH,
  deriveDocumentIdFromFilename,
  formatBytes,
  formatDate,
  statusLabel,
  statusTone,
}
export type { DocumentStatusTone }

const REFERENCE_TYPE_LABEL: Record<ReferenceType, string> =
  REFERENCE_TYPE_OPTIONS.reduce(
    (acc, opt) => {
      acc[opt.value] = opt.label
      return acc
    },
    {} as Record<ReferenceType, string>,
  )

export function referenceTypeLabel(type: string | null | undefined): string {
  if (!type) return '—'
  return REFERENCE_TYPE_LABEL[type as ReferenceType] ?? type
}

export function referenceTitle(doc: CatalogDocumentDTO): string {
  return (doc.title?.trim() || doc.filename?.trim() || doc.document_id).trim()
}

export function referenceSubtitle(doc: CatalogDocumentDTO): string {
  const parts: string[] = []
  if (doc.reference_type) parts.push(referenceTypeLabel(doc.reference_type))
  if (doc.reference_domain) parts.push(doc.reference_domain)
  if (doc.reference_year != null) parts.push(String(doc.reference_year))
  if (doc.reference_source) parts.push(doc.reference_source)
  return parts.join(' · ')
}

function foldForSearch(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
}

export function filterReferenceDocuments(
  docs: CatalogDocumentDTO[],
  query: string,
): CatalogDocumentDTO[] {
  const needle = foldForSearch(query.trim())
  if (!needle) return docs
  return docs.filter((doc) => {
    const haystack = foldForSearch(
      [
        doc.title ?? '',
        doc.filename ?? '',
        doc.document_id,
        doc.reference_type ?? '',
        doc.reference_domain ?? '',
        doc.reference_source ?? '',
        doc.reference_year != null ? String(doc.reference_year) : '',
      ].join(' '),
    )
    return haystack.includes(needle)
  })
}

export function sortReferenceDocumentsByRecency(
  docs: CatalogDocumentDTO[],
): CatalogDocumentDTO[] {
  return [...docs].sort((a, b) => {
    const aTime = new Date(a.indexed_at ?? a.created_at ?? 0).getTime()
    const bTime = new Date(b.indexed_at ?? b.created_at ?? 0).getTime()
    return bTime - aTime
  })
}

export function isDuplicateReferenceDocumentId(
  docs: CatalogDocumentDTO[],
  candidate: { tenantId: string; documentId: string },
): boolean {
  const id = candidate.documentId.trim().toLowerCase()
  if (!id) return false
  return docs.some(
    (d) =>
      d.tenant_id === candidate.tenantId &&
      d.document_kind === 'reference_document' &&
      d.document_id.trim().toLowerCase() === id,
  )
}

export function isValidReferenceYear(value: string): boolean {
  const trimmed = value.trim()
  if (!trimmed) return true // optional field
  if (!/^[0-9]{4}$/.test(trimmed)) return false
  const year = Number(trimmed)
  return year >= 1900 && year <= 2100
}
